import {
  ArrowRight,
  Award,
  BookOpenCheck,
  Briefcase,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  FlaskConical,
  GraduationCap,
  Handshake,
  Landmark,
  Lightbulb,
  LineChart,
  Microscope,
  Network,
  Route,
  ShieldCheck,
  Sparkles,
  Target,
  Trophy,
  Users,
} from "lucide-react";

const publicImage = name => `${process.env.PUBLIC_URL}/images/${name}`;

export const company = {
  name: "BrainTorch™ Bio",
  eyebrow: "Advancing Science. Empowering Minds.",
  primaryCta: "",
  email: "braintorchbio@gmail.com",
  phone: "(972) 914-8009",
  location: "Dallas–Fort Worth, Texas",
  linkedin: "https://www.linkedin.com/company/braintorch-bio/about/",
  heroTitle: "Building the Future Clinical Research Workforce",
  heroDescription:
    "Preparing future Clinical Research Coordinators, Research Assistants, and healthcare research professionals through translational science education and workforce-aligned training.",
};

export const links = {
  application: "",
  partnership: "",
  consultation: "",
  calendly: process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/onteriowright/new-meeting",
  linkedin: process.env.REACT_APP_LINKEDIN_URL || "https://www.linkedin.com/company/braintorch-bio/about/",
};

export const navItems = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  {
    label: "Services",
    path: "/services",
    children: [
      { label: "Scientific & Research Consulting", path: "/services/scientific-consulting" },
      { label: "STEM Education & Workforce Development", path: "/services/stem-workforce" },
      { label: "Mentorship & Career Development", path: "/services/mentorship" },
    ],
  },
  { label: "Programs", path: "/programs" },
  { label: "BrainTorch™ Launch", path: "/impact" },
  { label: "Partnerships", path: "/partnerships" },
  { label: "Contact", path: "/contact" },
  { label: "Blog", path: "/blog" },
];

export const homeStats = [
  {
    value: "Training",
    label: "STEM and clinical research education that builds foundational knowledge and skills.",
    icon: BookOpenCheck,
  },
  {
    value: "Exposure",
    label: "Real-world clinical research workflows, hands-on learning, and career pathway awareness.",
    icon: Microscope,
  },
  {
    value: "Readiness",
    label: "Professional development, mentorship, and workforce preparation for healthcare research careers.",
    icon: Briefcase,
  },
];

export const clientTypes = [
  {
    title: "STEM Education",
    description:
      "Foundational STEM education and enrichment programs that inspire students and prepare them for careers in science and health.",
    icon: GraduationCap,
  },
  {
    title: "Clinical Research Training",
    description:
      "Hands-on education in GCP, IRB, clinical operations, participant engagement, and research compliance.",
    icon: ClipboardCheck,
  },
  {
    title: "Workforce Development",
    description:
      "Career readiness, professional skills, mentorship, and STEM pathways for high-demand healthcare roles.",
    icon: Users,
  },
  {
    title: "Scientific Consulting",
    description:
      "Grant writing, scientific writing, clinical research strategy, and program management for research, education, and workforce initiatives.",
    icon: Microscope,
  },
];

export const servicePillars = [
  {
    title: "STEM Education Pathways",
    description:
      "Foundational STEM education and enrichment programs that inspire and prepare students for careers in science and health.",
    bullets: [
      "Science and health career exploration",
      "Translational science exposure",
      "Hands-on learning experiences",
      "Student-centered enrichment",
    ],
    icon: BookOpenCheck,
  },
  {
    title: "Clinical Research Training",
    description: "Competency-based training aligned to clinical research operations and entry-level workforce needs.",
    bullets: [
      "Clinical trial fundamentals",
      "GCP, IRB, ethics, and regulatory basics",
      "Participant engagement and documentation",
      "Study operations and data systems",
    ],
    icon: Microscope,
  },
  {
    title: "Career Readiness Programs",
    description: "Professional development that helps learners translate training into career momentum.",
    bullets: [
      "Resume and interview preparation",
      "Mentorship and confidence building",
      "Soft skills and workplace readiness",
      "Career pathway navigation",
    ],
    icon: LineChart,
  },
];

export const programHighlights = [
  {
    title: "Evidence-Based Curricula",
    text: "Training built around practical clinical research and translational science competencies.",
    icon: ClipboardCheck,
  },
  {
    title: "Expert-Led Instruction",
    text: "Instruction shaped by scientific, clinical research, and workforce development experience.",
    icon: Users,
  },
  {
    title: "Hands-On Learning",
    text: "Applied learning activities that connect classroom concepts to real research workflows.",
    icon: Microscope,
  },
  {
    title: "Certification Opportunities",
    text: "Career preparation that supports professional growth and credential readiness.",
    icon: Award,
  },
  {
    title: "Career Support",
    text: "Guidance for resumes, interviews, job readiness, and next-step planning.",
    icon: Briefcase,
  },
];

export const whoWeServe = [
  "High School Students",
  "Undergraduates",
  "Graduate Students",
  "Career Changers",
  "Early Career Professionals",
];

export const differentiators = [
  {
    title: "Industry-aligned curriculum",
    text: "BrainTorch™ Bio builds training around clinical research and healthcare workforce needs so participants can connect learning to career outcomes.",
    icon: BookOpenCheck,
  },
  {
    title: "Hands-on, applied learning model",
    text: "The training approach emphasizes applied learning, simulated trial work, and practical career readiness.",
    icon: ClipboardCheck,
  },
  {
    title: "Led by clinical neuroscience experience",
    text: "BrainTorch™ Bio is led by a translational and clinical research neuroscientist with real-world research experience.",
    icon: ShieldCheck,
  },
];

export const founderProfile = {
  name: "Keisha Smith Wade, PhD",
  role: "Founder & Scientific Leadership",
  credentials: "PhD",
  specialty: "Neuroscientist | Clinical Researcher | Educator",
  image: publicImage("about-dr-keisha-smith-wade.png"),
  imageAbout: publicImage("keisha-smith-wade-headshot.jpg"),
  quickFacts: [
    "Yale School of Medicine",
    "Translational Neuroscience",
    "Phase I-III Clinical Trials",
    "Scientific Program Leadership",
  ],
  bio: [
    "Dr. Keisha Smith Wade is a translational and clinical research neuroscientist professional with over a decade of experience spanning basic science, translational medicine, and clinical research operations in research environments.",
    "Her work has focused on neuropsychiatry, addiction neuroscience, mood disorders, women’s brain health, biomarker research, and clinical trial operations, with experience supporting multidisciplinary research initiatives at institutions including Yale School of Medicine.",
    "Throughout her career, Dr. Smith Wade has contributed to clinical and translational research programs involving neuroimaging, behavioral health, biomarker analysis, and FDA-regulated clinical research.",
    "Dr. Smith Wade founded BrainTorch™ Bio to help bridge the gap between STEM education, translational science, and workforce readiness within the growing healthcare and clinical research industries.",
  ],
};

export const aboutSections = [
  {
    title: "Our Mission",
    copy: "To ignite potential, build pathways, and empower future clinical research professionals through education, mentorship, and real-world experience.",
  },
  {
    title: "Our Vision",
    copy: "A clinical research workforce that is skilled, inclusive, innovative, and prepared to advance healthcare discovery.",
  },
  {
    title: "Who We Are",
    copy: "BrainTorch™ Bio is a Dallas–Fort Worth workforce training and consulting company focused on STEM education, clinical research training, and professional development.",
  },
];

export const servicesPageSections = [
  {
    heading: "STEM Education Pathways",
    text: "Foundational STEM education and enrichment programs that inspire and prepare students for careers in science and health.",
    checklist: [
      "Science and health career exploration",
      "Translational science exposure",
      "Hands-on learning experiences",
      "Student-centered enrichment",
    ],
    icon: BookOpenCheck,
  },
  {
    heading: "Clinical Research Training",
    text: "Competency-based clinical research education aligned to real-world study operations and entry-level workforce readiness.",
    checklist: [
      "Clinical trial fundamentals",
      "GCP, IRB, ethics, and regulatory basics",
      "Participant engagement and documentation",
      "Study operations and data systems",
    ],
    icon: Microscope,
  },
  {
    heading: "Career Readiness Programs",
    text: "Professional development workshops, soft skills training, and mentorship that help learners move from training to opportunity.",
    checklist: [
      "Resume and interview preparation",
      "Mentorship and career pathway guidance",
      "Professional communication and workplace readiness",
      "Job placement preparation and support",
    ],
    icon: LineChart,
  },
];

export const consultingSupport = [
  {
    tag: "Consulting Support",
    title: "Starter Consulting Support",
    summary: "Focused support for research, education, workforce, or program development needs.",
  },
  {
    tag: "Program Development",
    title: "Program Development Support",
    summary: "Support for organizations designing STEM, clinical research, or workforce initiatives.",
  },
  {
    tag: "Workforce Program Design",
    title: "Full Workforce Program Design",
    summary: "Comprehensive support for larger training, partnership, and workforce pipeline initiatives.",
  },
];

// Service cards for the Scientific & Research Consulting page
export const consultingServices = [
  {
    title: "Grant Writing & Proposal Development",
    description: "Proposal strategy, concept development, narrative writing, and funder-aligned messaging.",
    icon: FileText,
  },
  {
    title: "Scientific Writing & Editing",
    description: "Manuscripts, reports, abstracts, presentations, white papers, and program materials.",
    icon: BookOpenCheck,
  },
  {
    title: "Clinical Research Strategy",
    description: "Study planning, translational research alignment, recruitment strategy, and regulatory readiness.",
    icon: FlaskConical,
  },
  {
    title: "Program & Project Management",
    description: "Planning, timelines, stakeholder coordination, implementation, and evaluation.",
    icon: LineChart,
  },
];

// Service cards for the Mentorship & Career Development page
export const mentorshipServices = [
  {
    title: "Career Pathway Guidance",
    description: "One-on-one mentorship to navigate transitions into clinical research, STEM, and healthcare careers.",
    icon: Route,
  },
  {
    title: "Resume & Interview Preparation",
    description: "Resume review, interview coaching, and personalized feedback to strengthen job-readiness.",
    icon: ClipboardCheck,
  },
  {
    title: "Professional Skills Development",
    description:
      "Workplace communication, professional etiquette, and soft-skills training for emerging professionals.",
    icon: Users,
  },
  {
    title: "Networking & Connection",
    description: "Connecting mentees with industry professionals, alumni, and partner organizations.",
    icon: Network,
  },
];

export const processSteps = [
  "Select the right path: program, partnership inquiry, or consulting engagement.",
  "Review the learner, employer, workforce, education, or organization need.",
  "Align the program model, training timeline, partnership scope, or consulting support.",
  "Implement workforce training, STEM programming, clinical research support, or career development services.",
];

export const workforceApproach = [
  {
    title: "Educate",
    text: "Provide high-quality education and training in clinical research and translational science.",
    icon: BookOpenCheck,
  },
  { title: "Experience", text: "Offer hands-on internships and real-world research exposure.", icon: Users },
  { title: "Empower", text: "Build confidence, competence, and professional networks.", icon: Sparkles },
  {
    title: "Employ",
    text: "Connect talent with opportunities and prepare for long-term career success.",
    icon: Briefcase,
  },
];

export const workforcePipeline = [
  { title: "Training", text: "Build foundational knowledge and skills", icon: BookOpenCheck },
  { title: "Clinical Research Exposure", text: "Apply learning in real research settings", icon: Users },
  { title: "Internship", text: "Gain hands-on experience and mentorship", icon: ClipboardCheck },
  { title: "Workforce Readiness", text: "Prepare for meaningful careers", icon: LineChart },
  { title: "Career Placement", text: "Launch and grow in clinical research", icon: Briefcase },
];

export const focusAreas = [
  {
    title: "Diversity & Inclusion",
    text: "Expanding access and opportunity for underrepresented communities.",
    icon: Users,
  },
  { title: "Mentorship", text: "Connecting learners with experienced professionals.", icon: Network },
  { title: "Skill Development", text: "Technical, regulatory, and professional skill building.", icon: Lightbulb },
  {
    title: "Career Support",
    text: "Resume building, interview preparation, and job placement assistance.",
    icon: ClipboardCheck,
  },
];

export const partnershipModel = [
  { title: "Collaborate", text: "We work together with shared goals and mutual respect.", icon: Handshake },
  { title: "Innovate", text: "We co-create solutions that drive scientific and social impact.", icon: Lightbulb },
  { title: "Empower", text: "We expand opportunities for learners, researchers, and communities.", icon: Users },
  { title: "Impact", text: "We measure outcomes and celebrate collective success.", icon: Target },
];

export const partnerTypes = [
  { title: "Academic Institutions", text: "Universities, colleges, and training programs.", icon: GraduationCap },
  { title: "Healthcare Organizations", text: "Hospitals, clinics, and health systems.", icon: Building2 },
  { title: "Industry Partners", text: "Pharmaceutical, biotech, CRO, and MedTech companies.", icon: Landmark },
  { title: "Community Organizations", text: "Nonprofits and community groups driving local impact.", icon: Users },
];

export const partnerBenefits = [
  "Access to a pipeline of trained talent",
  "Collaborative research and innovation",
  "Community and social impact",
  "Visibility and recognition",
  "Long-term engagement",
];

export const careerReasons = [
  { title: "High Demand", text: "Growing need for skilled clinical research professionals.", icon: Users },
  { title: "Meaningful Impact", text: "Advance medical breakthroughs and improve patient lives.", icon: LineChart },
  { title: "Diverse Pathways", text: "Multiple career opportunities in healthcare and science.", icon: Route },
  { title: "Career Growth", text: "Strong earning potential and long-term advancement.", icon: Trophy },
];

export const impactHighlights = [
  {
    title: "Duration",
    text: "BrainTorch™ Launch is designed as a 6–7 month cohort-based clinical research training program.",
    icon: Target,
  },
  {
    title: "Schedule",
    text: "Program schedule: 9:00 AM – 2:30 PM.",
    icon: CheckCircle2,
  },
  {
    title: "Format",
    text: "Hybrid instruction plus applied learning.",
    icon: Building2,
  },
];

export const caseStudies = [
  {
    category: "Clinical Trial Skills",
    title: "What You’ll Learn",
    description:
      "Clinical trial fundamentals, regulatory compliance, patient assessments, recruitment, retention, study operations, and data systems.",
    result: "Participants build applied knowledge for entry-level clinical research and healthcare roles.",
  },
  {
    category: "Capstone Experience",
    title: "Simulated Clinical Trial Project",
    description:
      "Participants complete a simulated clinical trial project including recruitment, assessment, documentation, and presentation.",
    result: "The capstone connects program learning to practical trial operations.",
  },
  {
    category: "Career Outcomes",
    title: "Entry-Level Research Roles",
    description:
      "Graduates are prepared for roles such as Clinical Research Coordinator, Clinical Trial Assistant, and Research Assistant.",
    result: "The program is built around workforce readiness and job placement support.",
  },
];

export const resources = [
  {
    tag: "Career Support",
    title: "Mentorship & Career Development",
    summary:
      "Individualized support for resume and CV development, interview preparation, career pathway guidance, and graduate school advising.",
  },
  {
    tag: "Partnerships",
    title: "Partner With BrainTorch™ Bio",
    summary:
      "Employer partnerships, internship and training placements, workforce program collaborations, and grant or funding partnerships.",
  },
  ...consultingSupport,
  {
    tag: "Why Partner With Us",
    title: "Access to Trained Talent",
    summary:
      "Partners can connect with workforce-aligned training programs and customized program development support.",
  },
];

export const blogPosts = [
  {
    tag: "Clinical Research Careers",
    title: "How clinical research training creates career pathways",
    summary:
      "A look at how training, exposure, mentorship, and applied learning help learners move toward research careers.",
  },
  {
    tag: "Workforce Development",
    title: "Building a stronger research workforce",
    summary:
      "Why partnerships between education, healthcare, workforce, and industry partners matter for research readiness.",
  },
  {
    tag: "BrainTorch™ Launch",
    title: "What learners can expect from BrainTorch™ Launch",
    summary: "An overview of the training model, applied learning experience, and career readiness support.",
  },
];

export const bookingDetails = {
  title: "Schedule time with BrainTorch™ Bio",
  description:
    "Use the booking calendar to schedule a conversation about training programs, workforce development, partnerships, consulting support, or BrainTorch™ Launch.",
  bullets: ["Training and program questions", "Partnership conversations", "Consulting and workforce planning"],
};

export const contactReasons = [
  "Booking / Consultation",
  "Training Program",
  "Partnership",
  "Consulting",
  "BrainTorch™ Launch",
  "Blog / Media",
];

export const faqItems = [
  {
    question: "What is BrainTorch™ Launch?",
    answer:
      "BrainTorch™ Launch is a workforce training program designed to prepare participants for entry-level roles in clinical research.",
  },
  {
    question: "What roles can graduates prepare for?",
    answer:
      "Graduates are prepared for roles such as Clinical Research Coordinator, Clinical Trial Assistant, and Research Assistant.",
  },
  {
    question: "What partnership opportunities are available?",
    answer:
      "BrainTorch™ Bio collaborates on employer partnerships, internship and training placements, workforce program collaborations, and grant or funding partnerships.",
  },
];

export const footerNote =
  "BrainTorch™ Bio provides workforce training and technical expertise for STEM education and clinical research programs that prepare talent for high-demand healthcare careers.";

export const impactBanner = {
  title: "A cohort-based clinical research training program built for workforce readiness.",
  text: "BrainTorch™ Launch prepares participants through clinical trial fundamentals, regulatory compliance, patient assessments, recruitment and retention strategies, study operations, data systems, and a simulated clinical trial capstone.",
  icon: ArrowRight,
};

export const homeImages = {
  hero: publicImage("home-lab-hero.jpg"),
  wide: publicImage("home-lab-hero.jpg"),
  logo: publicImage("braintorch-logo.png"),
  symbol: publicImage("braintorch-symbol.png"),
  about: publicImage("about-dr-keisha-smith-wade.png"),
  programs: publicImage("programs-hero.jpg"),
  services: publicImage("programs-hero.jpg"),
  workforce: publicImage("workforce-hero.jpg"),
  partnerships: publicImage("partnerships-hero.jpg"),
};
